/**
 * The single place that decides what a health probe means.
 *
 * Yellow and red are not diagnostics. They ring a bell: customers see them on
 * the public status page and they phone in about them. So the burden of proof
 * sits on the alarm, not on the service. A colour changes only when a probe has
 * actually proved something. "I could not read the answer" proves nothing and
 * must stay green.
 *
 * The three rules that follow from that:
 *
 *   RED     the service is proved stopped and somebody has to redeploy or
 *           relaunch it. Nothing else earns red.
 *   YELLOW  there is a real action for somebody, short of a redeploy.
 *   GREEN   everything else. Slow answers, single blips, cold starts, and any
 *           check we could not read.
 *
 * Both the console status button and the public status page import this file.
 * They used to carry separate copies of these thresholds and had already
 * drifted apart, which is how the public page came to publish "Degraded" for a
 * service that was answering normally.
 */

/** A service the platform cannot run without. Only these can turn the button red. */
const CRITICAL = 'critical';
/** A service whose loss needs attention but not a redeploy. Caps at yellow. */
const SECONDARY = 'secondary';

const SERVICE_TIERS = {
    sam_dbam: CRITICAL,
    kernel: CRITICAL,
    console: CRITICAL,
    designer: CRITICAL,
    explorer: CRITICAL,
    channel_gate: CRITICAL,
    // Draft generation. Surveys still build, send, and collect without it.
    survey_assistant: SECONDARY,
    // Admin-only capability. Losing it needs a config fix, not a redeploy.
    identity_admin: SECONDARY,
    // Invitations stop, which is serious, but the fix is at the mail provider.
    email: SECONDARY,
    email_invitation_transport: SECONDARY,
};

function tierOf(key) {
    return SERVICE_TIERS[key] || SECONDARY;
}

/**
 * How many consecutive probes must agree before an alarm is shown.
 *
 * One failed poll is not evidence. A Cloud Run instance goes cold, a connection
 * resets, a revision swaps. Two agreeing polls costs about fifteen seconds of
 * delay on a genuine outage and removes every single-poll false alarm.
 */
const CONFIRMATIONS_REQUIRED = 2;

/**
 * Holds the consecutive-failure count per service across polls.
 *
 * An alarm has to be seen `required` times running before it is shown. Recovery
 * is immediate in the other direction: one good probe clears the streak, because
 * we want to stop ringing the bell as fast as possible.
 */
function createStatusConfirmer({ required = CONFIRMATIONS_REQUIRED } = {}) {
    const streaks = new Map();

    return function confirm(key, rawStatus) {
        if (rawStatus !== 'red' && rawStatus !== 'yellow') {
            streaks.delete(key);
            return rawStatus;
        }
        const seen = (streaks.get(key) || 0) + 1;
        streaks.set(key, seen);
        // Not yet proved. Stay quiet rather than flicker.
        return seen >= required ? rawStatus : 'green';
    };
}

/**
 * Turn one HTTP answer into a status.
 *
 * The governing idea: any answer at all means the process is running. A 404 or
 * a 401 proves the service is up and that our check is pointed at the wrong
 * place. That is our problem to fix, not a customer-facing fault, so it stays
 * green and is flagged with `checkIssue` for the operator view and the audit
 * log instead.
 *
 * Latency never affects the colour. A slow answer is still an answer, and a
 * cold start on a scale-to-zero service is normal operation.
 */
function classifyHttpStatus(httpStatus) {
    if (httpStatus >= 200 && httpStatus < 300) {
        return { status: 'green', summary: 'OK', errorCode: null, checkIssue: false };
    }
    if (httpStatus >= 500) {
        return {
            status: 'red',
            summary: `Server error (HTTP ${httpStatus})`,
            errorCode: `HTTP_${httpStatus}`,
            checkIssue: false,
        };
    }
    return {
        status: 'green',
        summary: `Answering, health path returned HTTP ${httpStatus}`,
        errorCode: `HTTP_${httpStatus}`,
        checkIssue: true,
    };
}

/** No answer inside the timeout. Real, but it takes two in a row to be shown. */
function timedOut() {
    return { status: 'red', summary: 'No answer', errorCode: 'TIMEOUT', checkIssue: false };
}

/** The connection itself failed: no DNS, refused, or TLS rejected. */
function unreachable() {
    return { status: 'red', summary: 'Unreachable', errorCode: 'UNREACHABLE', checkIssue: false };
}

/**
 * The browser would not let us read the answer, but the server did answer.
 * Cross-origin rules hid the status code from us. The service is running.
 */
function reachableOnly() {
    return { status: 'green', summary: 'Reachable', errorCode: null, checkIssue: false };
}

/** We never worked out where to look. A config gap in the checker, not an outage. */
function notChecked() {
    return { status: 'unknown', summary: 'Not checked', errorCode: 'URL_UNKNOWN', checkIssue: true };
}

/**
 * Fold every service into the one colour on the button and on the website.
 *
 * Only a critical service, proved stopped, turns it red. Everything else that
 * needs a human turns it yellow. `unknown` never rings the bell: a check we
 * could not run is not evidence of a fault.
 */
function aggregateStatus(services) {
    let anyAlarm = false;
    for (const service of services) {
        if (service.status === 'red' && tierOf(service.key) === CRITICAL) return 'red';
        if (service.status === 'red' || service.status === 'yellow') anyAlarm = true;
    }
    return anyAlarm ? 'yellow' : 'green';
}

module.exports = {
    CRITICAL,
    SECONDARY,
    SERVICE_TIERS,
    CONFIRMATIONS_REQUIRED,
    tierOf,
    createStatusConfirmer,
    classifyHttpStatus,
    timedOut,
    unreachable,
    reachableOnly,
    notChecked,
    aggregateStatus,
};
